/**
 * How far a long download has come, as numbers a screen can draw.
 *
 * A business with lakhs of entries arrives in about a hundred pages, and for
 * the minutes that takes every record without local rows used to draw a
 * skeleton and nothing else. The skeleton said "loading" and never said how
 * long. This is the part that can say it: the data that arrived, the data
 * still to come, and the fraction between them.
 *
 * Pure, so bun can test it. `sync-engine.ts` reaches `expo-sqlite` and
 * `expo/fetch` and cannot load under bun; that is why `sync-failure.ts` and
 * `outbox-drain.ts` live beside it for the same reason.
 *
 * THE TOTAL IS AN ESTIMATE, and it is labelled one on screen. The server
 * counts the ROWS still to come, because a count is one index read; it cannot
 * know the BYTES without building every page first. So the total size is the
 * rows still to come at the size per row seen so far. Rows of one record are
 * much the same size, so the estimate settles after the first page or two.
 */

/**
 * One long download, as the sync engine counts it.
 *
 * Two counts of rows, because two things happen and they do not keep step.
 * The next page is fetched while this one is written, so a page is RECEIVED
 * before it is SAVED. The bytes follow the pages received; the bar follows
 * the rows saved, because a row is only on the phone once it is written.
 */
export type DownloadProgress = {
  readonly tenantId: string;
  /** Entry versions this run will bring: the first page plus what the server said was left. */
  readonly rowsTotal: number;
  /** Entry versions that arrived over the network. */
  readonly rowsReceived: number;
  /** Entry versions written to this phone. */
  readonly rowsSaved: number;
  /** Body bytes as they came over the network, gzipped or not. */
  readonly bytesReceived: number;
};

/**
 * Whether a run is long enough to show.
 *
 * A first page that says there is more is a download of at least two pages,
 * which is thousands of rows. Anything smaller lands in about a second, and a
 * bar that appears and disappears in a second is noise.
 */
export const isLongDownload = (hasMore: boolean, remaining: number | undefined): boolean =>
  hasMore && remaining !== undefined && remaining > 0;

/** The progress once the first page of a long download has arrived, before it is written. */
export const startDownload = (input: {
  readonly tenantId: string;
  readonly rows: number;
  readonly remaining: number;
  readonly bytes: number;
}): DownloadProgress => ({
  tenantId: input.tenantId,
  rowsTotal: input.rows + input.remaining,
  rowsReceived: input.rows,
  rowsSaved: 0,
  bytesReceived: input.bytes,
});

/**
 * One more page arrived.
 *
 * `rowsTotal` grows if the rows pass it. That happens when somebody writes
 * during the download: the page bound moves with the horizon, and a bar at
 * more than 100% would be a lie about the size of the job.
 */
export const receivePage = (
  held: DownloadProgress,
  page: { readonly rows: number; readonly bytes: number },
): DownloadProgress => {
  const rowsReceived = held.rowsReceived + page.rows;
  return {
    ...held,
    rowsReceived,
    rowsTotal: Math.max(held.rowsTotal, rowsReceived),
    bytesReceived: held.bytesReceived + page.bytes,
  };
};

/** Some rows were written to this phone. */
export const saveRows = (held: DownloadProgress, rows: number): DownloadProgress => {
  const rowsSaved = held.rowsSaved + rows;
  return { ...held, rowsSaved, rowsTotal: Math.max(held.rowsTotal, rowsSaved) };
};

const BYTES_PER_MB = 1_000_000;

/** One decimal, so 0.4 MB is not drawn as 0 MB. */
const toMb = (bytes: number): number => Math.round((bytes / BYTES_PER_MB) * 10) / 10;

/**
 * What the card draws.
 *
 * The bar is the rows SAVED, since that is when the work is done. The total
 * size is the rows still to come at the size per row RECEIVED so far.
 */
export const presentDownload = (
  progress: DownloadProgress,
): {
  readonly fraction: number;
  readonly percent: number;
  readonly doneMb: number;
  readonly totalMb: number;
  readonly saved: number;
  readonly total: number;
} => {
  const fraction = progress.rowsTotal <= 0 ? 0 : Math.min(1, progress.rowsSaved / progress.rowsTotal);
  const perRow = progress.rowsReceived <= 0 ? 0 : progress.bytesReceived / progress.rowsReceived;
  const totalBytes = Math.max(progress.bytesReceived, perRow * progress.rowsTotal);
  return {
    fraction,
    percent: Math.floor(fraction * 100),
    doneMb: toMb(progress.bytesReceived),
    totalMb: toMb(totalBytes),
    saved: progress.rowsSaved,
    total: progress.rowsTotal,
  };
};
